<div class="accounts-page">
  <h1 class="page-title">Accounts & finance</h1>
  <p class="school-banner" *ngIf="schoolDisplayName">
    <mat-icon aria-hidden="true">apartment</mat-icon>
    {{ schoolDisplayName }}
  </p>
  <p class="page-sub">
    Fee structures by class and year, fee collection against student ledgers, and salary records. Data syncs to your API when
    available, and is cached in this browser as a fallback.
  </p>

  <div *ngIf="loading" class="loading-row">
    <mat-spinner diameter="40"></mat-spinner>
    <span>Loading finance data…</span>
  </div>

  <mat-tab-group *ngIf="!loading" animationDuration="0ms" class="accounts-tabs">
    <mat-tab label="Overview">
      <div class="tab-body">
        <div class="summary-grid">
          <mat-card>
            <mat-card-title>Fees</mat-card-title>
            <mat-card-content>
              <div class="stat"><span class="label">Annual expected</span><span class="value">{{ totalExpected() | number }}</span></div>
              <div class="stat"><span class="label">Collected</span><span class="value">{{ totalCollected() | number }}</span></div>
              <div class="stat"><span class="label">Outstanding</span><span class="value accent">{{ totalExpected() - totalCollected() | number }}</span></div>
            </mat-card-content>
          </mat-card>
          <mat-card>
            <mat-card-title>Payroll</mat-card-title>
            <mat-card-content>
              <div class="stat"><span class="label">Monthly salary load</span><span class="value">{{ totalSalaryMonthly() | number }}</span></div>
              <p class="hint">Salaries are planning rows; mark paid when payroll is run.</p>
            </mat-card-content>
          </mat-card>
        </div>
        <p class="api-hint">
          Backend: <code>GET/PUT /api/v1/finance/fee-structures</code>, <code>GET/PUT /api/v1/finance/student-ledgers</code>,
          <code>POST /api/v1/finance/payments</code>, <code>GET/PUT /api/v1/finance/salaries</code>
        </p>
      </div>
    </mat-tab>

    <mat-tab *ngIf="isAdmin" label="Fee structure">
      <div class="tab-body">
        <p class="tab-help">Define annual (or term) fee heads per class. Totals drive the “expected” amount on each student ledger.</p>
        <div class="toolbar">
          <mat-form-field appearance="outline">
            <mat-label>Academic year</mat-label>
            <input matInput [(ngModel)]="academicYear" (ngModelChange)="onAcademicYearChange()" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Class</mat-label>
            <mat-select [(ngModel)]="structClass" (selectionChange)="onStructClassChange()">
              <mat-option *ngFor="let c of classOptions" [value]="c">{{ c }}</mat-option>
            </mat-select>
          </mat-form-field>
          <button mat-flat-button color="primary" type="button" (click)="saveFeeStructure()">Save structure</button>
        </div>
        <table class="lines-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Label</th>
              <th>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let line of structLines; let i = index">
              <td><input class="cell" [(ngModel)]="line.code" /></td>
              <td><input class="cell" [(ngModel)]="line.label" /></td>
              <td><input class="cell" type="number" [(ngModel)]="line.amount" min="0" /></td>
              <td>
                <button mat-icon-button type="button" color="warn" (click)="removeFeeLine(i)" aria-label="Remove">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <button mat-stroked-button type="button" (click)="addFeeLine()">Add fee line</button>
      </div>
    </mat-tab>

    <mat-tab *ngIf="canCollectFees" label="Collect fees">
      <div class="tab-body">
        <p class="tab-help">Record payments against student balances. Ledger totals refresh when fee structures change.</p>
        <table mat-table [dataSource]="ledgerData" class="ledger-table">
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Student</th>
            <td mat-cell *matCellDef="let row">{{ row.name }}</td>
          </ng-container>
          <ng-container matColumnDef="className">
            <th mat-header-cell *matHeaderCellDef>Class</th>
            <td mat-cell *matCellDef="let row">{{ row.className }}</td>
          </ng-container>
          <ng-container matColumnDef="annualTotal">
            <th mat-header-cell *matHeaderCellDef>Expected</th>
            <td mat-cell *matCellDef="let row">{{ row.annualTotal | number }}</td>
          </ng-container>
          <ng-container matColumnDef="totalPaid">
            <th mat-header-cell *matHeaderCellDef>Paid</th>
            <td mat-cell *matCellDef="let row">{{ row.totalPaid | number }}</td>
          </ng-container>
          <ng-container matColumnDef="due">
            <th mat-header-cell *matHeaderCellDef>Due</th>
            <td mat-cell *matCellDef="let row">{{ due(row) | number }}</td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row">
              <button mat-stroked-button color="primary" type="button" (click)="selectStudentForPayment(row.studentId)">Pay</button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="ledgerColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: ledgerColumns"></tr>
        </table>

        <mat-card *ngIf="selectedLedger()" class="payment-card">
          <mat-card-title>Payment: {{ selectedLedger()?.name }}</mat-card-title>
          <mat-card-content>
            <form [formGroup]="paymentForm" (ngSubmit)="recordPayment()" class="pay-form">
              <mat-form-field appearance="outline">
                <mat-label>Amount</mat-label>
                <input matInput type="number" formControlName="amount" min="1" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Method</mat-label>
                <mat-select formControlName="method">
                  <mat-option *ngFor="let m of paymentMethods" [value]="m">{{ m }}</mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="grow">
                <mat-label>Remark</mat-label>
                <input matInput formControlName="remark" />
              </mat-form-field>
              <button mat-flat-button color="primary" type="submit" [disabled]="paymentForm.invalid">Record payment</button>
            </form>
            <h4 class="subhead">History</h4>
            <ul class="pay-history">
              <li *ngFor="let p of selectedLedger()?.payments">
                {{ p.date }} — {{ p.amount | number }} ({{ p.method }}) <span *ngIf="p.remark">— {{ p.remark }}</span>
              </li>
            </ul>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

    <mat-tab *ngIf="isAdmin" label="Salaries">
      <div class="tab-body">
        <p class="tab-help">Track monthly salary amounts by staff member. Use “Mark paid” with a month (YYYY-MM) after payroll.</p>
        <form [formGroup]="salaryForm" class="salary-add" (ngSubmit)="addSalary()">
          <mat-form-field appearance="outline">
            <mat-label>Staff name</mat-label>
            <input matInput formControlName="staffName" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Role</mat-label>
            <input matInput formControlName="role" placeholder="Teacher, Admin…" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Monthly amount</mat-label>
            <input matInput type="number" formControlName="monthlyAmount" min="0" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="grow">
            <mat-label>Notes</mat-label>
            <input matInput formControlName="notes" />
          </mat-form-field>
          <button mat-flat-button color="primary" type="submit" [disabled]="salaryForm.invalid">Add</button>
        </form>

        <div class="mark-paid-bar">
          <mat-form-field appearance="outline">
            <mat-label>Mark selected row paid for month</mat-label>
            <input matInput type="month" [(ngModel)]="markPaidMonth" name="markPaidMonth" />
          </mat-form-field>
          <span class="hint-inline">Select a row in the table via Edit flow — use row action below.</span>
        </div>

        <table mat-table [dataSource]="salaryData" class="salary-table">
          <ng-container matColumnDef="staffName">
            <th mat-header-cell *matHeaderCellDef>Name</th>
            <td mat-cell *matCellDef="let row">{{ row.staffName }}</td>
          </ng-container>
          <ng-container matColumnDef="role">
            <th mat-header-cell *matHeaderCellDef>Role</th>
            <td mat-cell *matCellDef="let row">{{ row.role }}</td>
          </ng-container>
          <ng-container matColumnDef="monthlyAmount">
            <th mat-header-cell *matHeaderCellDef>Monthly</th>
            <td mat-cell *matCellDef="let row">{{ row.monthlyAmount | number }}</td>
          </ng-container>
          <ng-container matColumnDef="paidForMonth">
            <th mat-header-cell *matHeaderCellDef>Last paid</th>
            <td mat-cell *matCellDef="let row">{{ row.paidForMonth || '—' }}</td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row">
              <button mat-button type="button" color="primary" (click)="markSalaryPaid(row)" [disabled]="!markPaidMonth">
                Mark paid
              </button>
              <button mat-icon-button type="button" color="warn" (click)="deleteSalary(row)" aria-label="Delete">
                <mat-icon>delete</mat-icon>
              </button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="salaryColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: salaryColumns"></tr>
        </table>
      </div>
    </mat-tab>
  </mat-tab-group>

  <mat-card *ngIf="!loading && isStudent" class="access-card">
    <mat-card-content>Fee balances and payments are managed by the school office. Contact administration for statements.</mat-card-content>
  </mat-card>
</div>
